import { Component, OnInit } from '@angular/core';
import { UserAuthService } from '../UserAuth.service';
import { Router } from '@angular/router';
import { ProjectService } from '../services/project.service';
import { HttpClient } from '@angular/common/http';
import { AuthUserParam, AuthResponse } from '../models/auth-user-param';
import { environment } from '../../environments/environment';
import { GoogleAuthService, GoogleAuthResponse } from '../services/google-auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  authStatusChceked = false;
  isAuthenticated = false;
  email = '';
  password = '';
  emailError = false;
  passwordError = false;
  isLoading = false;
  isGoogleLoading = false;

  constructor(
    private authService: UserAuthService,
    private projectService: ProjectService,
    private _router: Router,
    private http: HttpClient,
    private googleAuthService: GoogleAuthService
  ) {}

  ngOnInit(): void {
    console.log('ngOnInit Called. isAuthenticated:', this.isAuthenticated);

    // Subscribe to Authentication status
    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      //this.authStatusChceked = true;
      this.isAuthenticated = isAuthenticated;
      console.log(
        'Authentication Status Sub > isAuthenticated:',
        this.isAuthenticated
      );
      if (isAuthenticated) {
        // User is authenticated, check authorization
        this.authService.activeAccount$.subscribe((account) => {
          console.log(
            'login comp > Active Account subscribed after authentication:',
            account
          );
          if (account?.localAccountId) {
            this.checkAuthorization(account.tenantId, account.localAccountId);
          }
        });
      }
    });

    // Subscribe to Google authentication status
    this.googleAuthService.isGoogleAuthenticated$.subscribe((isGoogleAuth) => {
      console.log('Google Authentication Status:', isGoogleAuth);
      if (isGoogleAuth) {
        const googleUser = this.googleAuthService.getCurrentUser();
        console.log('Google User:', googleUser);
        if (googleUser?.tenantId) {
          console.log('Google tenant ID (Google Workspace domain):', googleUser.tenantId);
        } else if (googleUser?.hostedDomain) {
          console.log('Google hosted domain:', googleUser.hostedDomain);
        } else {
          console.log('Google tenant/hosted domain not present (personal Gmail account).');
        }
        
        // Complete the authentication process when Google auth succeeds
        this.completeGoogleAuthentication(googleUser);
      }
    });

    // Call the function to show the div after 3 seconds (3000 milliseconds)
    this.showLoginAreaAfterDelay(2000);

    //this.showLoginAreaAfterDelay(0);
  }

  onEmailLogin(): void {
    // Reset error states
    this.emailError = false;
    this.passwordError = false;

    // Validate fields
    if (!this.email || this.email.trim() === '') {
      this.emailError = true;
    }
    
    if (!this.password || this.password.trim() === '') {
      this.passwordError = true;
    }

    // If there are validation errors, don't proceed
    if (this.emailError || this.passwordError) {
      console.log('Validation failed - highlighting required fields');
      return;
    }

    // Start loading state
    this.isLoading = true;
    
    console.log('Email Login attempt for:', this.email);

    // Prepare request payload
    const authRequest: AuthUserParam = {
      username: this.email,
      password: this.password
    };

    // Call authentication API
    const apiUrl = `${environment.apiUrl}v1/authenticate`;
    console.log('Calling authentication API:', apiUrl);
    console.log('Request payload:', authRequest);
    
    // Configure request to expect text response instead of JSON
    this.http.post(apiUrl, authRequest, { responseType: 'text' })
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          console.log('Full API Response:', response);
          console.log('Response type:', typeof response);

          // Since we received HTTP 200, authentication is successful
          console.log('HTTP 200 received - authentication successful');

          // The response is a plain text string containing the tenant ID
          let tenantId: string | null = null;

          if (typeof response === 'string' && response.trim()) {
            // Remove any quotes from the response if present
            tenantId = response.replace(/^"(.*)"$/, '$1').trim();
            console.log('Extracted TenantId from text response:', tenantId);
          }

          // Store authentication data
          const tokenToStore = `temp_token_${Date.now()}`; // Generate temp token since API doesn't provide one
          const usernameToStore = this.email;

          localStorage.setItem('accessToken', tokenToStore);
          localStorage.setItem('username', usernameToStore);
          localStorage.setItem('isAuthenticated', 'true');

          // Store tenant ID if available
          if (tenantId) {
            localStorage.setItem('customerTenantId', tenantId);
            console.log('Stored tenant ID:', tenantId);
          } else {
            console.warn('No tenant ID found in authentication response');
          }

          console.log('Authentication successful - stored token and redirecting');
          console.log('Stored token:', tokenToStore);
          console.log('Stored username:', usernameToStore);

          // Update authentication service state to reflect email/password login
          this.authService.setLoginDisplay();
          
          // Navigate to main application and open project modal
          this._router.navigate(['/'], { queryParams: { openProjects: 'true' } });
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Authentication failed:', error);
          
          let errorMessage = 'Authentication failed';
          if (error.status === 400) {
            errorMessage = 'Invalid user parameters';
          } else if (error.status === 401) {
            errorMessage = 'Invalid email or password';
          } else if (error.status === 500) {
            errorMessage = 'Server error. Please try again later';
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }
          
          alert(errorMessage);
        }
      });
  }

  checkAuthorization(tenantId: string, objectId: string): void {
    this.projectService.checkUserAuthorization(tenantId, objectId).subscribe(
      (user) => {
        console.log('Authorization Check Response:', user);
        if (user) {
          console.log('User is authorized with roles:', user.roles);
          this.authService.setRolesFromCosmos(user);
          this._router.navigate(['/'], { queryParams: { openProjects: 'true' } });
        } else {
          console.log('User is not authorized');
          this.authService.setRolesFromCosmos(null);
          this._router.navigate(['/unauthorized']);
        }
      },
      (error) => {
        console.error('Authorization Check Error:', error);
        this.authService.setRolesFromCosmos(null);
        this._router.navigate(['/unauthorized']);
      }
    );
  }

  onMicrosoftLogin(): void {
    // Clear any validation errors
    this.emailError = false;
    this.passwordError = false;
    
    console.log('Microsoft Login');
    this.authService.login();
  }

  async onGoogleLogin(): Promise<void> {
    // Clear any validation errors
    this.emailError = false;
    this.passwordError = false;
    
    console.log('Google Login button clicked!');
    console.log('isGoogleLoading before:', this.isGoogleLoading);
    
    if (this.isGoogleLoading) {
      console.log('Google login already in progress, returning');
      return; // Prevent multiple simultaneous login attempts
    }

    console.log('Setting isGoogleLoading to true');
    this.isGoogleLoading = true;

    try {
      // Initiate Google OAuth redirect (like Microsoft login)
      // This will redirect to Google's login page
      await this.googleAuthService.login();
      
      // This code won't execute because we redirect away from the page
      // The authentication completion will be handled when Google redirects back
      
    } catch (error) {
      console.error('Google login failed:', error);
      this.isGoogleLoading = false;
      
      let errorMessage = 'Google login failed';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    }
  }

  /**
   * Complete Google authentication after OAuth callback
   */
  private completeGoogleAuthentication(googleUser: any): void {
    if (!googleUser) {
      console.error('No Google user data available');
      return;
    }

    console.log('Completing Google authentication for user:', googleUser);

    // Store authentication data (same format as other login methods)
    const tokenToStore = `google_token_${Date.now()}`;
    const usernameToStore = googleUser.email;
    
    localStorage.setItem('accessToken', tokenToStore);
    localStorage.setItem('username', usernameToStore);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('authProvider', 'google');
    
    // Use a default tenant ID for Google users
    localStorage.setItem('customerTenantId', 'google-default-tenant');
    
    // Store Google-specific data
    localStorage.setItem('googleUserName', googleUser.name);
    localStorage.setItem('googleUserPicture', googleUser.picture);

    console.log('Google authentication completed - stored data and redirecting');

    // Update authentication service state
    this.authService.setLoginDisplay();

    // Navigate to main application and open project modal
    this._router.navigate(['/'], { queryParams: { openProjects: 'true' } });
  }

  showLoginAreaAfterDelay(delayMilliseconds: number): void {
    if (!this.authStatusChceked) {
      setTimeout(() => {
        this.authStatusChceked = true;
      }, delayMilliseconds);
    }
  }

}
